import React from 'react';
import { Handle, Position } from 'reactflow';
import { FileText, Database, Trash2, Play } from 'lucide-react';
import { NodeData } from '../../../types';
import { useProjectStore, useFlowStore } from '../../../store';
import ConfirmationDialog from '../../common/ConfirmationDialog';
import { NodeLabel, NodeBadge } from './shared';
import { useDeleteStep } from '../../../hooks/queries/useStepMutations';
import { useModels } from '../../../hooks/queries/useModels';

interface AutoPromptNodeProps {
  data: NodeData;
  selected: boolean;
}

const AutoPromptNode: React.FC<AutoPromptNodeProps> = ({ data, selected }) => {
  const [isDeletable, setIsDeletable] = React.useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);

  let projectStore: any;
  let flowStore: any;

  // Try to call the hooks - they might be mocked functions
  try {
    const projectHook = useProjectStore as any;
    const flowHook = useFlowStore as any;

    // If it's a mock function, it should have been set up to return an object
    projectStore = typeof projectHook === 'function' ? projectHook() : projectHook;
    flowStore = typeof flowHook === 'function' ? flowHook() : flowHook;
  } catch (e) {
    console.error('Error calling store hooks:', e);
    projectStore = {};
    flowStore = { setSelectedNode: () => {}, nodes: [], edges: [], setNodes: () => {}, setEdges: () => {} };
  }

  // AC-1412 / AC-1377 P5.1: project models flow through React Query
  // (`useModels`) instead of `useProjectStore.models`. Tests that mock
  // `useProjectStore` to return `{ models: [...] }` continue to work via
  // the legacy fallback below; production reads from RQ.
  const currentProjectId = projectStore?.currentProject?.project_id;
  const modelsQuery = useModels(currentProjectId);
  // Legacy mock support: some unit tests mock useProjectStore to return
  // a fixed `models` array. Prefer the RQ result, but fall back to the
  // mock-provided slice so those tests keep passing without a rewrite.
  const models = (modelsQuery.data && modelsQuery.data.length > 0)
    ? modelsQuery.data
    : (projectStore?.models || []);
  const setSelectedNode = flowStore?.setSelectedNode || (() => {});
  const nodes = flowStore?.nodes || [];
  const edges = flowStore?.edges || [];
  const setNodes = flowStore?.setNodes || (() => {});
  const setEdges = flowStore?.setEdges || (() => {});

  // AC-1406: Delete via React Query mutation hook. Tests that render this
  // node MUST wrap it in a `QueryClientProvider` (e.g. via the
  // `renderWithQueryClient` helper) — `useDeleteStep` calls
  // `useQueryClient` internally and will throw "No QueryClient set" if
  // the context is missing.
  const deleteStepMutation = useDeleteStep(data.step?.orchestration_id ?? '');

  // Check if node can be deleted (not referenced by other nodes)
  React.useEffect(() => {
    if (!data.step || !data.step.orchestration_step_id) {
      setIsDeletable(false);
      return;
    }
    const canDelete = !nodes.some(
      (n: any) =>
        n.id !== data.id &&
        n.data?.step?.previous_step_id &&
        data.step?.orchestration_step_id &&
        String(n.data.step.previous_step_id) === String(data.step.orchestration_step_id)
    );
    setIsDeletable(canDelete);
  }, [nodes, data.id, data.step?.orchestration_step_id]);

  const handleDelete = async () => {
    if (!data.step?.orchestration_step_id) return;

    try {
      await deleteStepMutation.mutateAsync({
        stepId: data.step.orchestration_step_id,
      });

      // Remove the node and its connected edges from local canvas state
      // for an immediate UX response. The mutation hook's invalidation
      // refreshes the steps cache; the seed effect will rebuild from it.
      setNodes(nodes.filter((node: any) => node.id !== data.id));
      setEdges(edges.filter((edge: any) => edge.source !== data.id && edge.target !== data.id));

      setSelectedNode(null);
    } catch (error) {
      console.error('Failed to delete step:', error);
    }
  };

  const outputModel = data.step?.auto_prompt_model_id
    ? models.find((m: any) => m.model_id === data.step?.auto_prompt_model_id)
    : undefined;

  // Determine output mode display
  const getOutputModeDisplay = () => {
    if (data.step?.auto_prompt_output_mode === 'text') {
      return {
        text: 'Text Mode',
        icon: <FileText className="w-3 h-3" />,
        colorClass: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
        iconTestId: 'output-mode-text-icon'
      };
    } else if (data.step?.auto_prompt_output_mode === 'model') {
      return {
        text: outputModel?.class_name || 'Schema Mode',
        icon: <Database className="w-3 h-3" data-testid="database-icon" />,
        colorClass: 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300',
        iconTestId: 'database-icon'
      };
    }
    // Fallback
    return {
      text: 'Auto Prompt',
      icon: <FileText className="w-3 h-3" />,
      colorClass: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
      iconTestId: 'output-mode-fallback-icon'
    };
  };

  const outputModeInfo = getOutputModeDisplay();

  // Check if this is the first step (initiator)
  const isFirstStep = !data.step?.previous_step_id;

  return (
    <div className="relative">
      <div
        data-testid="auto-prompt-card"
        className={`relative w-72 h-32 px-4 py-3 rounded-lg border-2 shadow-lg transition-all duration-200
          ${selected ? 'ring-2 ring-primary-500 ring-opacity-50' : ''}
          ${isFirstStep ? 'border-green-500' : 'border-gray-300 dark:border-gray-600'}
          bg-gradient-to-br from-gray-100 to-white dark:from-gray-800 dark:to-gray-900
          hover:shadow-xl hover:border-opacity-100`}
      >
        {/* Start badge - show when this is the first step, clickable to start execution.
            Positioned outside the card (top-left corner) so it doesn't overlap content. */}
        {isFirstStep && (
          <button
              title="Click to start orchestration"
              onClick={(e) => {
                e.stopPropagation();
                data.onStartExecution?.();
              }}
              className="absolute -top-3 -left-2 px-2 py-1 rounded-full bg-gradient-to-r from-green-500 to-green-600 text-white text-xs font-semibold shadow-md whitespace-nowrap z-20 flex items-center gap-1 cursor-pointer hover:from-green-600 hover:to-green-700 hover:shadow-lg active:scale-95 transition-all duration-150"
            >
              <Play className="w-3 h-3" />
              <span>Start</span>
            </button>
        )}

        {/* Delete button - only show if node is deletable */}
        {isDeletable && (
          <button
              title="Delete step"
              onClick={(e) => {
                e.stopPropagation();
                setShowDeleteConfirm(true);
              }}
              className="absolute -top-2 -right-2 p-1 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors z-50"
            >
              <Trash2 className="w-4 h-4" />
            </button>
        )}

        {/* Target handles for incoming connections */}
        <Handle
          type="target"
          position={Position.Top}
          id="top"
          style={{ top: -12, left: '50%', transform: 'translate(-50%, -50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white"
        />
        <Handle
          type="target"
          position={Position.Left}
          id="left"
          style={{ left: -8, top: '50%', transform: 'translate(-50%, -50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white"
        />
        <Handle
          type="target"
          position={Position.Right}
          id="right"
          style={{ right: -8, top: '50%', transform: 'translate(50%, -50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white"
        />
        <Handle
          type="target"
          position={Position.Bottom}
          id="bottom"
          style={{ left: '50%', bottom: -12, transform: 'translate(-50%, 50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white"
        />

        {/* Primary Badge - Auto Prompt with teal/cyan gradient */}
        <div
          data-testid="auto-prompt-primary-badge"
          className="absolute -top-3 left-1/2 transform -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r from-teal-500 to-cyan-600 text-white text-sm font-semibold tracking-wide shadow-md whitespace-nowrap z-20"
        >
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4" data-testid="file-text-icon" />
            <span>Auto Prompt</span>
          </div>
        </div>

        {/* Main Card Content */}
        <div className="flex flex-col items-center justify-center h-full">
          {/* Output Mode Badge */}
          <NodeBadge
            icon={outputModeInfo.icon}
            label={outputModeInfo.text}
            colorScheme={outputModeInfo.colorClass}
            testId="output-mode-badge"
          />

          {/* Node Label (optional, if different from "Auto Prompt") */}
          {data.label && data.label !== 'Auto Prompt' && (
            <NodeLabel
              label={data.label}
              maxLength={20}
              fontWeight="normal"
              textColor="text-gray-600 dark:text-gray-400"
              textSize="sm"
              className="mt-2 px-2"
            />
          )}
        </div>

        {/* Source handles for outgoing connections */}
        <Handle
          type="source"
          position={Position.Top}
          id="top"
          style={{ top: -12, left: '50%', transform: 'translate(-50%, -50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white"
        />
        <Handle
          type="source"
          position={Position.Left}
          id="left"
          style={{ left: -8, top: '50%', transform: 'translate(-50%, -50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white"
        />
        <Handle
          type="source"
          position={Position.Right}
          id="right"
          style={{ right: -8, top: '50%', transform: 'translate(50%, -50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white"
        />
        <Handle
          type="source"
          position={Position.Bottom}
          id="bottom"
          style={{ left: '50%', bottom: -12, transform: 'translate(-50%, 50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white"
        />
      </div>

      {/* Delete confirmation dialog */}
      {showDeleteConfirm && (
        <ConfirmationDialog
          title="Delete Step"
          message="Are you sure you want to delete this step? This action cannot be undone."
          confirmText="Delete"
          confirmVariant="danger"
          onConfirm={() => {
            handleDelete();
            setShowDeleteConfirm(false);
          }}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}
    </div>
  );
};

export default AutoPromptNode;